import { normalizeAppHostedUploadUrl } from './normalizeAppHostedUploadUrl';
import { parseServerImageUrls } from './mediaUploadLimits';
import type { AdminEditorBlockFields } from './adminEditorBlockTypes';

export type RichContentLegacyApiFields = {
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  imageUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  videoUrl?: string;
};

function pickApiString(row: Record<string, unknown>, camel: string, pascal: string): string | undefined {
  const value = row[camel] ?? row[pascal];
  if (typeof value === 'string') return value;
  if (value == null) return undefined;
  return String(value);
}

/** Normalize quiz/exercise question or option rows from API (camelCase or PascalCase). */
export function coerceRichContentLegacyFieldsFromApi(source: unknown): RichContentLegacyApiFields {
  if (!source || typeof source !== 'object') return {};
  const row = source as Record<string, unknown>;
  return {
    textContent: pickApiString(row, 'textContent', 'TextContent'),
    textContentFa: pickApiString(row, 'textContentFa', 'TextContentFa'),
    textFormatting: pickApiString(row, 'textFormatting', 'TextFormatting'),
    textFormattingFa: pickApiString(row, 'textFormattingFa', 'TextFormattingFa'),
    imageUrl: pickApiString(row, 'imageUrl', 'ImageUrl'),
    audioUrl: pickApiString(row, 'audioUrl', 'AudioUrl'),
    audioFileName: pickApiString(row, 'audioFileName', 'AudioFileName'),
    videoUrl: pickApiString(row, 'videoUrl', 'VideoUrl'),
  };
}

/** Read questionOptions / QuestionOptions from API question rows. */
export function readApiQuestionOptionsList(source: unknown): unknown[] {
  if (!source || typeof source !== 'object') return [];
  const row = source as Record<string, unknown>;
  const options = row.questionOptions ?? row.QuestionOptions;
  return Array.isArray(options) ? options : [];
}

function resolveEditorBlockType(raw: unknown): AdminEditorBlockFields['type'] {
  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase();
    if (normalized === 'image') return 'image';
    if (normalized === 'audio') return 'audio';
    if (normalized === 'video') return 'video';
    if (normalized === 'textaudio' || normalized === 'text-audio') return 'text';
    return 'text';
  }
  if (typeof raw === 'number') {
    // ContentBlockType: 0=text, 1=audio, 3=image, 4=video
    if (raw === 3) return 'image';
    if (raw === 1) return 'audio';
    if (raw === 4) return 'video';
    return 'text';
  }
  return 'text';
}

function rawBlockHasEditorShape(raw: Record<string, unknown>): boolean {
  return (
    'type' in raw ||
    'Type' in raw ||
    'blockType' in raw ||
    'BlockType' in raw
  );
}

function normalizeApiEditorBlock(raw: unknown, index: number): AdminEditorBlockFields | null {
  if (!raw || typeof raw !== 'object') return null;
  const block = raw as Record<string, unknown>;
  if (!rawBlockHasEditorShape(block)) return null;
  const type = resolveEditorBlockType(block.type ?? block.Type ?? block.blockType ?? block.BlockType);
  const idRaw = block.id ?? block.Id;
  return normalizeLoadedBlock({
    id: typeof idRaw === 'string' ? idRaw : undefined,
    type,
    textContent: pickApiString(block, 'textContent', 'TextContent'),
    textContentFa: pickApiString(block, 'textContentFa', 'TextContentFa'),
    textFormatting: pickApiString(block, 'textFormatting', 'TextFormatting'),
    textFormattingFa: pickApiString(block, 'textFormattingFa', 'TextFormattingFa'),
    imageUrl: pickApiString(block, 'imageUrl', 'ImageUrl'),
    audioUrl: pickApiString(block, 'audioUrl', 'AudioUrl'),
    audioFileName: pickApiString(block, 'audioFileName', 'AudioFileName'),
    videoUrl: pickApiString(block, 'videoUrl', 'VideoUrl'),
    originalAudioUrl: pickApiString(block, 'originalAudioUrl', 'OriginalAudioUrl'),
    voiceStylePresetId: pickApiString(block, 'voiceStylePresetId', 'VoiceStylePresetId'),
    transcript: pickApiString(block, 'transcript', 'Transcript'),
    displayOrder:
      typeof block.displayOrder === 'number'
        ? block.displayOrder
        : typeof block.DisplayOrder === 'number'
          ? block.DisplayOrder
          : index,
  });
}

function normalizeApiEditorBlocksJson(parsed: unknown): AdminEditorBlockFields[] | null {
  if (!Array.isArray(parsed) || parsed.length === 0) return null;
  const blocks = parsed
    .map((raw, index) => normalizeApiEditorBlock(raw, index))
    .filter((block): block is AdminEditorBlockFields => block != null);
  return blocks.length > 0 ? blocks : null;
}

export function isEditorBlocksJson(value: unknown): value is AdminEditorBlockFields[] {
  if (!Array.isArray(value) || value.length === 0) return false;
  const first = value[0];
  return typeof first === 'object' && first !== null && rawBlockHasEditorShape(first as Record<string, unknown>);
}

function normalizeLoadedBlock(block: AdminEditorBlockFields): AdminEditorBlockFields {
  const next: AdminEditorBlockFields = { ...block };
  if (next.type === 'image' && next.imageUrl) {
    const urls = parseServerImageUrls(next.imageUrl);
    next.imageUrl =
      urls.length <= 1
        ? normalizeAppHostedUploadUrl(urls[0] ?? next.imageUrl)
        : JSON.stringify(urls.map((url) => normalizeAppHostedUploadUrl(url)));
  }
  return next;
}

export function parseEditorBlocksFromApi(item: RichContentLegacyApiFields): AdminEditorBlockFields[] | null {
  if (item.textFormatting) {
    try {
      const parsed = JSON.parse(item.textFormatting) as unknown;
      const normalized = normalizeApiEditorBlocksJson(parsed);
      if (normalized) {
        return normalized.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
      }
    } catch {
      /* legacy rich-text formatting */
    }
  }
  return null;
}

export function convertLegacyItemToEditorBlocks(item: {
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  imageUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  videoUrl?: string;
}): AdminEditorBlockFields[] {
  const blocks: AdminEditorBlockFields[] = [];
  let blockOrder = 0;

  const legacyText = (item.textContent ?? '').trim();
  const legacyTextFa = (item.textContentFa ?? '').trim();
  const isStructuralMarker =
    legacyText === '__COMMENT__' ||
    legacyText === '__PAGE_SEPARATOR__' ||
    legacyTextFa === '__COMMENT__' ||
    legacyTextFa === '__PAGE_SEPARATOR__';
  if (!isStructuralMarker && (legacyText || legacyTextFa)) {
    blocks.push({
      type: 'text',
      textContent: item.textContent,
      textContentFa: item.textContentFa,
      textFormatting: item.textFormatting,
      textFormattingFa: item.textFormattingFa,
      displayOrder: blockOrder++,
    });
  }
  if (item.imageUrl && item.imageUrl.trim()) {
    blocks.push({
      type: 'image',
      imageUrl: normalizeAppHostedUploadUrl(item.imageUrl),
      displayOrder: blockOrder++,
    });
  }
  if (item.audioUrl && item.audioUrl.trim()) {
    blocks.push({
      type: 'audio',
      audioUrl: normalizeAppHostedUploadUrl(item.audioUrl),
      audioFileName: item.audioFileName,
      displayOrder: blockOrder++,
    });
  }
  if (item.videoUrl && item.videoUrl.trim()) {
    blocks.push({
      type: 'video',
      videoUrl: item.videoUrl,
      displayOrder: blockOrder++,
    });
  }

  return blocks;
}

export function sortEditorBlocksByDisplayOrder<T extends { displayOrder?: number }>(blocks: T[]): T[] {
  return [...blocks].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
}

/** Stable key for pending audio/image/video editor state; survives block reorder within a scope. */
export function adminEditorBlockMediaKey(
  scopeKey: string,
  block: { id?: string },
  index: number
): string {
  const stableId = typeof block.id === 'string' ? block.id.trim() : '';
  return stableId ? `${scopeKey}--${stableId}` : `${scopeKey}--i${index}`;
}

export function ensureEditorBlockClientId(block: { id?: string }, order: number): string {
  const existing = typeof block.id === 'string' ? block.id.trim() : '';
  if (existing) return existing;
  return `block-${order}-${Math.random().toString(36).slice(2, 11)}`;
}

export function normalizeEditorBlocksFromApi<T extends AdminEditorBlockFields & { id?: string }>(
  blocks: T[]
): T[] {
  return sortEditorBlocksByDisplayOrder(blocks).map((block, order) => ({
    ...block,
    id: ensureEditorBlockClientId(block, order),
    displayOrder: block.displayOrder ?? order,
  }));
}

export function updateSortedEditorBlockAtIndex<T extends { displayOrder?: number }>(
  blocks: T[],
  sortedIndex: number,
  patch: Partial<T>
): T[] {
  const sorted = sortEditorBlocksByDisplayOrder(blocks);
  if (sortedIndex < 0 || sortedIndex >= sorted.length) return blocks;
  const next = sorted.map((b, i) => (i === sortedIndex ? { ...b, ...patch } : b));
  return next.map((b, idx) => ({ ...b, displayOrder: idx }));
}

export function removeSortedEditorBlockAtIndex<T extends { displayOrder?: number }>(
  blocks: T[],
  sortedIndex: number
): T[] {
  const sorted = sortEditorBlocksByDisplayOrder(blocks);
  if (sortedIndex < 0 || sortedIndex >= sorted.length) return blocks;
  const next = sorted.filter((_, i) => i !== sortedIndex);
  return next.map((b, idx) => ({ ...b, displayOrder: idx }));
}

export function loadEditorBlocksFromApi(item: unknown): AdminEditorBlockFields[] {
  return mapApiRowToEditorBlocks(item);
}

/** Map a quiz/exercise API question or option row into admin editor blocks. */
export function mapApiRowToEditorBlocks(source: unknown): AdminEditorBlockFields[] {
  const coerced = coerceRichContentLegacyFieldsFromApi(source);
  if (source && typeof source === 'object') {
    const row = source as Record<string, unknown>;
    const questionFallback = pickApiString(row, 'question', 'Question')?.trim();
    if (
      questionFallback &&
      questionFallback !== '__COMMENT__' &&
      questionFallback !== '__PAGE_SEPARATOR__' &&
      !questionFallback.startsWith('__COMMENT__\n') &&
      !questionFallback.startsWith('__PAGE_SEPARATOR__\n') &&
      !coerced.textContent?.trim() &&
      !coerced.textContentFa?.trim()
    ) {
      coerced.textContent = questionFallback;
    }
  }
  const loaded = parseEditorBlocksFromApi(coerced) ?? convertLegacyItemToEditorBlocks(coerced);
  if (loaded.length > 0) {
    return loaded;
  }
  return [];
}

const QUIZ_STRUCTURE_MARKERS = new Set(['__COMMENT__', '__PAGE_SEPARATOR__']);

export function isQuizStructureMarkerText(value?: string | null): boolean {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return false;
  if (QUIZ_STRUCTURE_MARKERS.has(trimmed)) return true;
  return trimmed.startsWith('__COMMENT__\n') || trimmed.startsWith('__PAGE_SEPARATOR__\n');
}

export type EditorBlockLike = {
  type?: string;
  textContent?: string;
  textContentFa?: string;
  imageUrl?: string;
  audioUrl?: string;
  originalAudioUrl?: string;
  videoUrl?: string;
};

/** True when a block has text or uploaded/recorded media (not an empty placeholder). */
export function editorBlockHasSubstantiveContent(block: EditorBlockLike): boolean {
  if (block.type === 'text') {
    return !!((block.textContent?.trim()) || (block.textContentFa?.trim()));
  }
  if (block.type === 'image') return !!(block.imageUrl?.trim());
  if (block.type === 'audio') {
    return !!(block.audioUrl?.trim() || block.originalAudioUrl?.trim());
  }
  if (block.type === 'video') return !!(block.videoUrl?.trim());
  return false;
}

export function editorBlocksHaveSubstantiveContent(blocks?: EditorBlockLike[] | null): boolean {
  return (blocks ?? []).some(editorBlockHasSubstantiveContent);
}

/** Legacy rich-text: remove HTML only. Preserves spaces, ZWNJ (نیم‌فاصله), and other characters. */
export function stripHtmlFromEditorPlainText(value?: string | null): string {
  if (!value) return '';
  if (!value.includes('<') && !value.includes('&nbsp;')) {
    return value;
  }
  return value.replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ');
}

/** @deprecated Use stripHtmlFromEditorPlainText — old helper trimmed/collapsed spaces. */
export function stripEditorPlainText(value?: string | null): string {
  return stripHtmlFromEditorPlainText(value);
}

export function getPlainTextFromOptionBlocks(blocks?: EditorBlockLike[] | null): string {
  const textBlock = (blocks ?? []).find((b) => b.type === 'text');
  return stripHtmlFromEditorPlainText(textBlock?.textContent);
}

export function plainTextOptionHasContent(blocks?: EditorBlockLike[] | null): boolean {
  return getPlainTextFromOptionBlocks(blocks).trim().length > 0;
}

export function buildSinglePlainTextOptionBlocks<T extends EditorBlockLike & { id?: string; displayOrder?: number }>(
  blocks: T[] | undefined,
  text: string
): T[] {
  const existing = (blocks ?? []).find((b) => b.type === 'text');
  return [
    {
      ...(existing ?? { id: `block-${Date.now()}-${Math.random().toString(36).slice(2, 9)}` }),
      type: 'text',
      textContent: text,
      textContentFa: undefined,
      textFormatting: undefined,
      textFormattingFa: undefined,
      displayOrder: 0,
    } as T,
  ];
}

export function normalizePlainTextOptionBlocksFromApi<T extends EditorBlockLike & { id?: string; displayOrder?: number }>(
  blocks: T[]
): T[] {
  const plain = getPlainTextFromOptionBlocks(blocks);
  if (!plainTextOptionHasContent(blocks) && !(blocks ?? []).some((b) => b.type === 'text')) {
    return blocks;
  }
  return buildSinglePlainTextOptionBlocks(blocks, plain);
}

/** Persist puzzle / fill-in-blank option answers as a single plain text block (no rich formatting). */
export function flattenPlainTextOptionBlocksForApi(blocks?: AdminEditorBlockFields[]) {
  const text = getPlainTextFromOptionBlocks(blocks);
  const blockId =
    (typeof blocks?.find((b) => b.type === 'text')?.id === 'string'
      ? blocks!.find((b) => b.type === 'text')!.id
      : undefined) ?? `block-0-${Math.random().toString(36).slice(2, 9)}`;

  if (!text) {
    return {
      textContent: '',
      textFormatting: undefined,
      textContentFa: undefined,
      textFormattingFa: undefined,
      imageUrl: undefined,
      audioUrl: undefined,
      audioFileName: undefined,
      videoUrl: undefined,
    };
  }

  const plainBlock = {
    id: blockId,
    type: 'text',
    textContent: text,
    displayOrder: 0,
  };

  return {
    textContent: text,
    textContentFa: undefined,
    textFormatting: JSON.stringify([plainBlock]),
    textFormattingFa: undefined,
    imageUrl: undefined,
    audioUrl: undefined,
    audioFileName: undefined,
    videoUrl: undefined,
  };
}

export function flattenEditorBlocksForApi<T extends AdminEditorBlockFields>(
  blocks: T[] = []
): {
  textContent?: string;
  textContentFa?: string;
  textFormatting?: string;
  textFormattingFa?: string;
  imageUrl?: string;
  audioUrl?: string;
  audioFileName?: string;
  videoUrl?: string;
} {
  const result: {
    textContent?: string;
    textContentFa?: string;
    textFormatting?: string;
    textFormattingFa?: string;
    imageUrl?: string;
    audioUrl?: string;
    audioFileName?: string;
    videoUrl?: string;
  } = {};

  const sortedBlocks = [...blocks].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  const textBlock = sortedBlocks.find((b) => b.type === 'text');
  if (textBlock) {
    result.textContent = textBlock.textContent || '';
    result.textContentFa = textBlock.textContentFa || undefined;
  }

  const imageBlock = sortedBlocks.find((b) => b.type === 'image' && (b.imageUrl || '').trim());
  const audioBlock = sortedBlocks.find((b) => b.type === 'audio' && (b.audioUrl || '').trim());
  const videoBlock = sortedBlocks.find((b) => b.type === 'video' && (b.videoUrl || '').trim());

  if (imageBlock?.imageUrl) result.imageUrl = imageBlock.imageUrl;
  if (audioBlock?.audioUrl) {
    result.audioUrl = audioBlock.audioUrl;
    if (audioBlock.audioFileName) result.audioFileName = audioBlock.audioFileName;
  }
  if (videoBlock?.videoUrl) result.videoUrl = videoBlock.videoUrl;

  if (blocks.length > 0) {
    result.textFormatting = JSON.stringify(sortedBlocks);
  }

  return result;
}
